import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Button,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  RestaurantMenu as RestaurantMenuIcon,
  AccessTime as AccessTimeIcon,
  LocalFireDepartment as CaloriesIcon,
  FitnessCenter as ProteinIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { getMealTypeColor, getGreyBackground } from "../theme/ClientTheme";

/**
 * Detailed view for a specific day showing all meals and recipes
 * Allows clicking on recipes to view full recipe details
 */
const ClientDayView = ({
  day,
  mealPlan,
  recipes,
  onRecipeSelect,
  onBackToWeekly,
  weeklySchedule,
  dailyNutritionTotals,
}) => {
  const theme = useTheme();
  const daySchedule =
    weeklySchedule?.[day] || mealPlan.weeklySchedule?.[day] || {};
  const mealTypes = ["breakfast", "lunch", "dinner", "snack"];

  const formatDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  const getMealTypeIcon = (mealType) => {
    const icons = {
      breakfast: "🌅",
      lunch: "☀️",
      dinner: "🌙",
      snack: "🍎",
    };
    return icons[mealType] || "🍽️";
  };

  const getRecipesByMealType = (mealType) => {
    if (weeklySchedule?.[day]?.mealTypes) {
      // Use processed weekly schedule structure
      const scheduledRecipes = daySchedule.mealTypes?.[mealType] || [];
      return scheduledRecipes.map((sr) => sr.recipe).filter(Boolean);
    } else {
      // Fall back to original meal plan structure
      const recipeIds = daySchedule[mealType] || [];
      return recipeIds
        .map((recipeId) => recipes.find((r) => r.id === recipeId))
        .filter(Boolean);
    }
  };

  const calculateDayTotals = () => {
    console.log(
      "Debug: calculateDayTotals called with day:",
      day,
      "recipes:",
      recipes,
      "weeklySchedule:",
      weeklySchedule,
      "mealPlan:",
      mealPlan,
    );

    // Use processed daily nutrition totals if available
    if (dailyNutritionTotals?.[day]?.total) {
      const totals = dailyNutritionTotals[day].total;

      // Still need to calculate totalTime from recipes
      let totalTime = 0;
      mealTypes.forEach((mealType) => {
        getRecipesByMealType(mealType).forEach((recipe) => {
          totalTime +=
            recipe.instructions?.reduce((sum, step) => {
              if (typeof step.duration !== "number") {
                console.warn(
                  `Warning: Step in recipe "${recipe.name}" has an invalid or missing duration. Defaulting to 0.`,
                );
              }
              return sum + (step.duration || 0);
            }, 0) || 0;
        });
      });

      return {
        totalCalories: Math.round(totals.kCals || 0),
        totalProtein: Math.round(totals.proteins || 0),
        totalCarbs: Math.round(totals.carbs || 0),
        totalFats: Math.round(totals.fats || 0),
        totalFiber: Math.round(totals.fiber || 0),
        totalTime: totalTime,
      };
    }

    const recipeFiber = (recipe) =>
      parseFloat(recipe.fiber) ||
      parseFloat(recipe.mac?.fi) ||
      parseFloat(recipe.mac?.fiber) ||
      0;

    // Fall back to manual calculation
    let totalCalories = 0;
    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFats = 0;
    let totalFiber = 0;
    let totalTime = 0;

    mealTypes.forEach((mealType) => {
      getRecipesByMealType(mealType).forEach((recipe) => {
        if (weeklySchedule?.[day]?.mealTypes) {
          // Use processed data - already accounts for servings
          totalCalories += Math.round(recipe.kCals || 0);
          totalProtein += Math.round(recipe.proteins || 0);
          totalCarbs += Math.round(recipe.carbs || 0);
          totalFats += Math.round(recipe.fats || 0);
          totalFiber += Math.round(recipeFiber(recipe));
        } else {
          // Use per-serving calculation
          const perServing = {
            calories: Math.round((recipe.kCals || 0) / recipe.servings),
            protein: Math.round((recipe.proteins || 0) / recipe.servings),
            carbs: Math.round((recipe.carbs || 0) / recipe.servings),
            fats: Math.round((recipe.fats || 0) / recipe.servings),
            fiber: Math.round(recipeFiber(recipe) / (recipe.servings || 1)),
          };

          totalCalories += perServing.calories;
          totalProtein += perServing.protein;
          totalCarbs += perServing.carbs;
          totalFats += perServing.fats;
          totalFiber += perServing.fiber;
        }
        console.log("Debug: Recipe name:", recipe.name);
        console.log("Debug: Recipe instructions:", recipe.instructions);
        if (recipe.instructions) {
          recipe.instructions.forEach((step, index) => {
            console.log(`Debug: Step ${index + 1} duration:`, step.duration);
          });
        } else {
          console.warn(`Warning: Recipe "${recipe.name}" has no instructions.`);
        }
        totalTime += recipe.instructions?.reduce((sum, step) => {
          if (typeof step.duration !== "number") {
            console.warn(
              `Warning: Step in recipe "${recipe.name}" has an invalid or missing duration. Defaulting to 0.`,
            );
          }
          return sum + (step.duration || 0);
        }, 0);
      });
    });

    return {
      totalCalories,
      totalProtein,
      totalCarbs,
      totalFats,
      totalFiber,
      totalTime,
    };
  };

  const dayTotals = calculateDayTotals();

  console.log("Debug: Calculated dayTotals:", dayTotals);

  return (
    <Box>
      {/* Header with back button */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackToWeekly}
          variant="outlined"
          sx={{
            textTransform: "none",
            "& .MuiButton-startIcon": {
              marginRight: { xs: 0, sm: 0.5 },
            },
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            Back to Weekly View
          </Box>
        </Button>
        <Typography variant="h3" sx={{ fontWeight: 600 }}>
          {formatDayName(day)} Meal Plan
        </Typography>
      </Stack>

      {/* Daily nutrition summary */}
      <Card
        sx={{ mb: 4, bgcolor: "primary.light", color: "primary.contrastText" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Daily Nutrition Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {dayTotals.totalCalories}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Calories
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {dayTotals.totalProtein}g
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Protein
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {dayTotals.totalCarbs}g
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Carbs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {dayTotals.totalFats}g
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Fats
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {dayTotals.totalFiber}g
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Fiber
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Meals by type */}
      <Grid container spacing={3}>
        {mealTypes.map((mealType) => {
          const mealRecipes = getRecipesByMealType(mealType);

          if (mealRecipes.length === 0) return null;

          return (
            <Grid item xs={12} md={6} key={mealType}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Meal type header */}
                  <Box sx={{ mb: 3 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="h2" sx={{ fontSize: "1.5rem" }}>
                        {getMealTypeIcon(mealType)}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                      </Typography>
                      <Chip
                        label={`${mealRecipes.length} recipe${mealRecipes.length > 1 ? "s" : ""}`}
                        size="small"
                        sx={{
                          bgcolor: getMealTypeColor(mealType, theme),
                          color: "white",
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Recipes for this meal type */}
                  <Stack spacing={2}>
                    {mealRecipes.map((recipe) => {
                      // Calculate per-serving nutrition with proper fallback
                      const servings = recipe.servings || 1;

                      const perServing = {
                        calories: Math.round((recipe.kCals || 0) / servings),
                        protein: Math.round((recipe.proteins || 0) / servings),
                        carbs: Math.round((recipe.carbs || 0) / servings),
                        fats: Math.round((recipe.fats || 0) / servings),
                        fiber: Math.round(
                          ((parseFloat(recipe.fiber) ||
                            parseFloat(recipe.mac?.fi) ||
                            parseFloat(recipe.mac?.fiber) ||
                            0) /
                            servings),
                        ),
                      };

                      return (
                        <Card
                          key={recipe.id}
                          variant="outlined"
                          sx={{
                            transition: "all 0.2s ease",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardActionArea
                            onClick={() => onRecipeSelect(recipe)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Typography
                                variant="h6"
                                sx={{ mb: 1, fontWeight: 600 }}
                              >
                                {recipe.name}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2, lineHeight: 1.4 }}
                              >
                                {recipe.description}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={2}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "success.light",
                                      width: 20,
                                      height: 20,
                                    }}
                                  >
                                    <CaloriesIcon sx={{ fontSize: 12 }} />
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {perServing.calories} kcal
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: "error.light",
                                      width: 20,
                                      height: 20,
                                    }}
                                  >
                                    <ProteinIcon sx={{ fontSize: 12 }} />
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {perServing.protein}g protein
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "primary.main",
                                    }}
                                  >
                                    C: {perServing.carbs}g
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "warning.main",
                                    }}
                                  >
                                    F: {perServing.fats}g
                                  </Typography>
                                </Box>

                                {perServing.fiber > 0 && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: "success.main",
                                      }}
                                    >
                                      Fi: {perServing.fiber}g
                                    </Typography>
                                  </Box>
                                )}

                                {recipe.totalTime && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "warning.light",
                                        width: 20,
                                        height: 20,
                                      }}
                                    >
                                      <AccessTimeIcon sx={{ fontSize: 12 }} />
                                    </Avatar>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {recipe.totalTime} min
                                    </Typography>
                                  </Box>
                                )}
                              </Stack>

                              {/* Dietary restrictions */}
                              {recipe.dietaryRestrictions &&
                                recipe.dietaryRestrictions.length > 0 && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 2 }}
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    {recipe.dietaryRestrictions.map(
                                      (restriction, index) => (
                                        <Chip
                                          key={index}
                                          label={restriction}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: "0.75rem" }}
                                        />
                                      ),
                                    )}
                                  </Stack>
                                )}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Day summary */}
      <Card sx={{ mt: 4, bgcolor: getGreyBackground(theme) }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Day Summary
          </Typography>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RestaurantMenuIcon color="primary" />
              <Typography variant="body1">
                <strong>
                  {mealTypes.reduce(
                    (total, type) => total + getRecipesByMealType(type).length,
                    0,
                  )}
                </strong>{" "}
                total meals
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon color="warning" />
              <Typography variant="body1">
                <strong>~{Math.round(dayTotals.totalTime / 60)}</strong> minutes
                total prep time
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientDayView;
